import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AllCommunityModule, ColDef, ICellRendererParams, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { themeZafire } from '@zafire/ag-grid-enterprise';
import { ZfBox, ZfTypography } from '@zafire/react';
import { StatusPill } from 'views/PrivateBanking/StatusPill';
import './CountryRiskWaiversGrid.css';

ModuleRegistry.registerModules([AllCommunityModule]);

export interface WaiverRow {
  id: string;
  waiver: string;
  status: string | undefined;
}

const StatusCell = ({ value }: ICellRendererParams<WaiverRow, string>) => (
  <ZfBox style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
    <StatusPill status={value ?? undefined} clickable={false} />
  </ZfBox>
);

const NoWaiversOverlay = () => {
  const { t } = useTranslation();
  return (
    <ZfTypography variant="title-medium" color="muted">
      {t('pb.countryriskwaivers.grid.empty')}
    </ZfTypography>
  );
};

type Props = {
  rows: WaiverRow[];
  testId: string;
};

export const CountryRiskWaiversGrid = ({ rows, testId }: Props) => {
  const { t } = useTranslation();

  const columnDefs: ColDef<WaiverRow>[] = useMemo(
    () => [
      { headerName: t('pb.countryriskwaivers.grid.column.waiver'), field: 'waiver', flex: 1 },
      { headerName: t('pb.countryriskwaivers.grid.column.status'), field: 'status', flex: 1, cellRenderer: StatusCell },
    ],
    [t],
  );

  return (
    <div
      data-testid={testId}
      className={rows.length > 0 ? 'country-risk-waivers-grid--has-rows' : undefined}
      style={{ boxSizing: 'border-box' }}
    >
      <AgGridReact<WaiverRow>
        rowData={rows}
        columnDefs={columnDefs}
        getRowId={({ data }) => data.id}
        domLayout="autoHeight"
        suppressCellFocus
        noRowsOverlayComponent={NoWaiversOverlay}
        theme={themeZafire}
      />
    </div>
  );
};
